import { AbstractControl, ValidationErrors } from '@angular/forms';
import { Address } from 'src/app/admin-api/model/address';
import { ResponseB4AAddress } from 'src/app/admin-api/model/responseB4AAddress';
import { TypedError } from 'src/app/models';
import { ViaCepResponse } from 'src/app/models/viaCepResponse';

export const EDIT_PERSON_ADDRESS_ROLES = [
  'ROLE_ADMIN',
  'ROLE_SAC',
  'ROLE_USER_CS',
  'ROLE_CS_AGENT'
] as const;

export const INVALID_CEP_MESSAGE = 'CEP inválido. Informe 8 dígitos.';

export const SUBSCRIBER_CEP_FREIGHT_NOTE =
  'Alterar o CEP deste endereço recalcula somente o frete da assinatura (shippingPrice). O valor da assinatura, cobranças e pedidos de envio não são alterados.';

export const ADDRESS_UPDATE_ERROR_FALLBACK = 'Erro ao atualizar o endereço.';

export interface PersonAddressFormValue {
  addressType: Address.AddressTypeEnum | null;
  city: string | null;
  country: string | null;
  neighborhood: string | null;
  phone: string | null;
  postalCode: string | null;
  recipientName: string | null;
  state: string | null;
  street: string | null;
  streetExtension: string | null;
  streetNumber: string | null;
}

export interface PersonAddressUpdateRequest {
  addressType: Address.AddressTypeEnum | null;
  city: string | null;
  country: string | null;
  neighborhood: string | null;
  phone: string | null;
  postalCode: string | null;
  recipientName: string | null;
  state: string | null;
  street: string | null;
  streetExtension: string | null;
  streetNumber: string | null;
}

export interface AddressSaveResult {
  address: Address;
  previousShippingPrice?: number;
}

export function normalizePostalCode(
  value: string | null | undefined
): string | null {
  if (value === null || value === undefined) return null;
  return value.replace(/\D/g, '');
}

export function nullableText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return value;
}

export function buildPersonAddressUpdateRequest(
  value: PersonAddressFormValue
): PersonAddressUpdateRequest {
  return {
    addressType: value.addressType ?? null,
    city: nullableText(value.city),
    country: nullableText(value.country),
    neighborhood: nullableText(value.neighborhood),
    phone: nullableText(value.phone),
    postalCode: normalizePostalCode(value.postalCode),
    recipientName: nullableText(value.recipientName),
    state: nullableText(value.state),
    street: nullableText(value.street),
    streetExtension: nullableText(value.streetExtension),
    streetNumber: nullableText(value.streetNumber)
  };
}

export function postalCodeValidator(
  control: AbstractControl
): ValidationErrors | null {
  const digits = normalizePostalCode(control.value);
  if (!digits) return null;
  return digits.length === 8 ? null : { postalCode: true };
}

export function personAddressUpdateUrl(
  apiUrl: string,
  personId: number,
  addressId: number
): string {
  const base = apiUrl.replace(/\/$/, '');
  return `${base}/address/${encodeURIComponent(String(personId))}/${encodeURIComponent(String(addressId))}`;
}

export function unwrapUpdatedAddress(
  body: ResponseB4AAddress | Address | null | undefined
): Address {
  if (!body) {
    throw new Error('Resposta vazia ao atualizar endereço.');
  }
  if ('result' in body && body.result) {
    return body.result;
  }
  if ('addressId' in body || 'postalCode' in body) {
    return body as Address;
  }
  throw new Error('Resposta vazia ao atualizar endereço.');
}

export function apiErrorMessage(
  error: TypedError | undefined | null,
  fallback = ADDRESS_UPDATE_ERROR_FALLBACK
): string {
  return error?.error?.message || error?.message || fallback;
}

export function shippingPriceChangeMessage(
  previous: number | null | undefined,
  next: number | null | undefined
): string | null {
  if (previous == null && next == null) return null;
  const previousNumber = previous == null ? null : Number(previous);
  const nextNumber = next == null ? null : Number(next);
  if (previousNumber === nextNumber) return null;
  return `Frete da assinatura: ${formatBrl(previousNumber)} → ${formatBrl(nextNumber)}`;
}

export function viaCepAddressPatch(
  data: ViaCepResponse | null | undefined
): Partial<PersonAddressFormValue> | null {
  if (!data || data.erro) return null;
  const patch: Partial<PersonAddressFormValue> = {};
  if (data.logradouro) patch.street = data.logradouro;
  if (data.bairro) patch.neighborhood = data.bairro;
  if (data.localidade) patch.city = data.localidade;
  if (data.uf) patch.state = data.uf;
  return Object.keys(patch).length ? patch : null;
}

function formatBrl(value: number | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}
